import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SpellCheck2, Check, ArrowRight, Wand2, Eye } from "lucide-react";

// Vorschau der automatischen KI-Prüfung beim Speichern: Rechtschreibkorrekturen +
// Betreff-Vorschlag + Vorschauzeile (Preheader). Der Nutzer wählt, was übernommen wird.
export function SpellcheckDialog({
  open,
  onOpenChange,
  diffs = [],
  currentSubject = "",
  suggestedSubject = "",
  suggestedPreview = "",
  onSave,
  onSkip,
  saving,
}) {
  const hasSubject = !!suggestedSubject && suggestedSubject.trim() !== (currentSubject || "").trim();
  const hasPreview = !!suggestedPreview;
  const [useSubject, setUseSubject] = useState(true);
  const [usePreview, setUsePreview] = useState(true);

  useEffect(() => {
    if (open) {
      setUseSubject(hasSubject);
      setUsePreview(hasPreview);
    }
  }, [open, hasSubject, hasPreview]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="spellcheck-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SpellCheck2 className="w-5 h-5 text-primary" /> KI-Prüfung vor dem Speichern
          </DialogTitle>
          <DialogDescription>
            Vorschläge zur Verbesserung. Wähle, was übernommen werden soll.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[55vh] pr-3">
          <div className="space-y-4">
            {diffs.length > 0 && (
              <div className="space-y-2" data-testid="spellcheck-section-diffs">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <SpellCheck2 className="w-4 h-4 text-primary" /> Rechtschreibung &amp; Grammatik ({diffs.length})
                </div>
                {diffs.map((d, i) => (
                  <div key={i} className="rounded-lg border p-3 text-sm" data-testid={`spellcheck-diff-${i}`}>
                    <div className="text-xs font-medium text-muted-foreground mb-1.5">{d.label}</div>
                    <div className="line-through text-destructive/80 whitespace-pre-wrap">{d.before}</div>
                    <div className="flex items-start gap-1.5 mt-1 text-foreground">
                      <ArrowRight className="w-3.5 h-3.5 mt-1 shrink-0 text-primary" />
                      <span className="whitespace-pre-wrap font-medium">{d.after}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasSubject && (
              <label className="flex gap-3 rounded-lg border p-3 cursor-pointer" data-testid="spellcheck-subject-suggestion">
                <Checkbox checked={useSubject} onCheckedChange={(v) => setUseSubject(!!v)} className="mt-0.5" data-testid="spellcheck-use-subject" />
                <div className="space-y-1 text-sm">
                  <div className="font-semibold flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-primary" /> Knackigerer Betreff</div>
                  {currentSubject && <div className="text-xs text-muted-foreground line-through">{currentSubject}</div>}
                  <div className="font-medium">{suggestedSubject}</div>
                </div>
              </label>
            )}

            {hasPreview && (
              <label className="flex gap-3 rounded-lg border p-3 cursor-pointer" data-testid="spellcheck-preview-suggestion">
                <Checkbox checked={usePreview} onCheckedChange={(v) => setUsePreview(!!v)} className="mt-0.5" data-testid="spellcheck-use-preview" />
                <div className="space-y-1 text-sm">
                  <div className="font-semibold flex items-center gap-1.5"><Eye className="w-4 h-4 text-primary" /> Vorschauzeile (Postfach-Vorschau)</div>
                  <div className="font-medium">{suggestedPreview}</div>
                </div>
              </label>
            )}
          </div>
        </ScrollArea>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onSkip} disabled={saving} data-testid="spellcheck-reject-btn">
            Unverändert speichern
          </Button>
          <Button type="button" onClick={() => onSave(useSubject, usePreview)} disabled={saving} data-testid="spellcheck-accept-btn">
            <Check className="w-4 h-4 mr-2" /> Übernehmen &amp; speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
